// /Users/pramod/Desktop/school-erp/frontend/src/Tenant-Admin/WebsiteManager/sections/RecruitersSection.js

import React, { useState, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiX, FiImage } from 'react-icons/fi';
import api from '../../../api';

const RecruitersSection = ({ websiteData, handleInputChange, handleSave, tenantId }) => {
  const recruiters = websiteData?.recruiters || { items: [] };

  // Local state configurations to fully decouple text input typing from parent updates
  const [localTitle, setLocalTitle] = useState(recruiters.sectionTitle || '');
  const [localSubtitle, setLocalSubtitle] = useState(recruiters.sectionSubtitle || '');
  const [localDisplayStyle, setLocalDisplayStyle] = useState(recruiters.displayStyle || 'logo-wall');
  const [localCheckboxes, setLocalCheckboxes] = useState({
    showHires: recruiters.showHires !== false,
    showCategories: recruiters.showCategories !== false
  });
  const [localItems, setLocalItems] = useState(recruiters.items || []);

  const [newItem, setNewItem] = useState({
    name: '',
    category: 'Technology',
    hires: 0,
    link: ''
  });
  const [uploading, setUploading] = useState({});
  const [saving, setSaving] = useState(false);

  // Sync isolated state from parent only on clean initial mount or tenant context switch
  useEffect(() => {
    console.log('[RecruitersSection] Initializing isolated field vectors from base source properties.');
    const sec = websiteData?.recruiters || {};
    setLocalTitle(sec.sectionTitle || '');
    setLocalSubtitle(sec.sectionSubtitle || '');
    setLocalDisplayStyle(sec.displayStyle || 'logo-wall');
    setLocalCheckboxes({
      showHires: sec.showHires !== false,
      showCategories: sec.showCategories !== false
    });
    setLocalItems(sec.items || []);
  }, [websiteData?.recruiters, tenantId]); // Fixed missing dependency by explicitly including websiteData?.recruiters

  const handleToggle = () => {
    handleInputChange('recruiters', 'isActive', !recruiters.isActive);
  };

  const handleGlobalFieldChange = (field, value) => {
    console.log(`[RecruitersSection] Local field altered: Key [${field}] -> Value [${value}]`);
    if (field === 'sectionTitle') setLocalTitle(value);
    if (field === 'sectionSubtitle') setLocalSubtitle(value);
    if (field === 'displayStyle') setLocalDisplayStyle(value);
  };

  const handleLocalCheckboxToggle = (key) => {
    setLocalCheckboxes(prev => {
      const nextState = { ...prev, [key]: !prev[key] };
      console.log(`[RecruitersSection] Toggling binary selection [${key}]:`, nextState[key]);
      return nextState;
    });
  };

  const handleAddItem = () => {
    if (!newItem.name) return;
    
    const item = {
      _id: Date.now().toString(),
      name: newItem.name,
      category: newItem.category,
      hires: Number(newItem.hires) || 0,
      link: newItem.link,
      logo: ''
    };
    
    const updatedItems = [...localItems, item];
    console.log('[RecruitersSection] Appending fresh structural partner card locally:', item);
    setLocalItems(updatedItems);
    
    // Sync array data blocks up instantly to protect rendering list consistency
    handleInputChange('recruiters', 'items', updatedItems);
    setNewItem({ name: '', category: 'Technology', hires: 0, link: '' });
  };

  const handleRemoveItem = (index) => {
    const updatedItems = localItems.filter((_, i) => i !== index);
    console.log('[RecruitersSection] Extracting partner row item local position index:', index);
    setLocalItems(updatedItems);
    
    // Sync array data blocks up instantly to protect rendering list consistency
    handleInputChange('recruiters', 'items', updatedItems);
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], [field]: field === 'hires' ? (value === '' ? '' : Number(value)) : value };
    
    console.log(`[RecruitersSection] row typing change tracking [${index}] -> Field [${field}] -> Value [${value}]`);
    setLocalItems(updatedItems);
  };

  const handleLogoUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      alert('Image size must be less than 2MB');
      event.target.value = '';
      return;
    }

    // Generate immediate object preview stream locally for instant display feedback
    const localBlobPreview = URL.createObjectURL(file);
    const previewItems = [...localItems];
    previewItems[index] = { ...previewItems[index], logo: localBlobPreview };
    setLocalItems(previewItems);

    setUploading(prev => ({ ...prev, [index]: true }));

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('section', 'recruiters');
      formData.append('itemIndex', index);
      formData.append('subField', 'logo');

      const response = await api.post(`/api/website/${tenantId}/upload/recruiters`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        const fileUrl = response.data.data?.url || response.data.data;
        console.log('[RecruitersSection] Server asset upload resolved successfully path string:', fileUrl);
        const updatedItems = [...localItems];
        updatedItems[index] = { ...updatedItems[index], logo: fileUrl };
        setLocalItems(updatedItems);
        handleInputChange('recruiters', 'items', updatedItems);
      } else {
        alert(response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert(error.response?.data?.message || 'Failed to upload logo');
    } finally {
      setUploading(prev => ({ ...prev, [index]: false }));
      event.target.value = '';
    }
  };

  const resolveAssetUrl = (urlPath) => {
    if (!urlPath) return '';
    if (typeof urlPath === 'string' && (urlPath.includes('tenants%2Fnull') || urlPath.includes('tenants/null'))) {
      return '';
    }
    const base = (api?.defaults?.baseURL || '').replace(/\/+$/, '') || (process.env.REACT_APP_BACKEND_URL || '').replace(/\/+$/, '');
    if (typeof urlPath === 'object') {
      if (urlPath.url) return urlPath.url.startsWith('/') ? (base ? `${base}${urlPath.url}` : urlPath.url) : urlPath.url;
      if (urlPath.fileId) return base ? `${base}/api/files/tenant-website/${urlPath.fileId}` : `/api/files/tenant-website/${urlPath.fileId}`;
      return '';
    }
    if (/^https?:\/\//i.test(urlPath)) return urlPath;
    if (urlPath.startsWith('/')) return base ? `${base}${urlPath}` : urlPath;
    return urlPath;
  };

  const handleRemoveLogo = (index) => {
    const updatedItems = [...localItems];
    updatedItems[index] = { ...updatedItems[index], logo: '' };
    setLocalItems(updatedItems);
    handleInputChange('recruiters', 'items', updatedItems);
  };

  const handleSaveClick = async () => {
    setSaving(true);
    console.log('[RecruitersSection] Transporting local memory arrays and variables to top layout configuration block.');
    
    try {
      // Synchronously match internal fields parameters to global parent tree maps
      await handleInputChange('recruiters', 'sectionTitle', localTitle);
      await handleInputChange('recruiters', 'sectionSubtitle', localSubtitle);
      await handleInputChange('recruiters', 'displayStyle', localDisplayStyle);
      await handleInputChange('recruiters', 'showHires', localCheckboxes.showHires);
      await handleInputChange('recruiters', 'showCategories', localCheckboxes.showCategories);
      await handleInputChange('recruiters', 'items', localItems);

      const assembledPayload = {
        ...recruiters,
        sectionTitle: localTitle,
        sectionSubtitle: localSubtitle,
        displayStyle: localDisplayStyle,
        showHires: localCheckboxes.showHires,
        showCategories: localCheckboxes.showCategories,
        items: localItems
      };

      await handleSave('recruiters', assembledPayload);
    } catch (error) {
      console.error('Failed to save recruiters section:', error);
    } finally {
      setSaving(false);
    }
  };

  const categories = ['Technology', 'Consulting', 'Finance', 'Education', 'Healthcare', 'Manufacturing', 'Retail', 'Other'];

  return (
    <div className="mt-4 mx-2 sm:mx-0 bg-slate-50 rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden">
      
      {/* Top Header Row Panel */}
      <div className="bg-white p-4 sm:p-6 border-b border-slate-200">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-bold text-slate-900 tracking-tight">Recruiters & Partners</h2>
            <p className="text-sm text-slate-500 mt-0.5">Showcase corporate companies that recruit your campus graduates</p>
          </div>
          
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:space-x-3">
            <button
              type="button"
              onClick={handleToggle}
              className={`flex items-center justify-center px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${
                recruiters.isActive !== false
                  ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  : 'bg-slate-100 text-slate-600 border border-slate-200'
              }`}
            >
              {recruiters.isActive !== false ? (
                <>
                  <span className="w-2 h-2 rounded-full bg-emerald-500 mr-2" />
                  Active
                </>
              ) : (
                <>
                  <span className="w-2 h-2 rounded-full bg-slate-400 mr-2" />
                  Inactive
                </>
              )}
            </button>

            <button
              type="button"
              onClick={handleSaveClick}
              disabled={saving}
              className={`flex items-center justify-center px-5 py-2.5 rounded-xl text-sm font-semibold shadow-sm transition-all ${
                saving 
                  ? 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700 active:scale-[0.98]'
              }`}
            >
              <FiSave className="mr-2 h-4 w-4" />
              {saving ? 'Saving...' : 'Save All'}
            </button>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 space-y-6">
        
        {/* Layout Parameter Setup Inputs */}
        <div className="bg-white rounded-xl p-4 sm:p-5 border border-slate-200/80 shadow-sm space-y-4">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-400">Section Configuration</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Header Title</label>
              <input
                type="text"
                value={localTitle}
                onChange={(e) => handleGlobalFieldChange('sectionTitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Our Recruiters"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Subtitle Row</label>
              <input
                type="text"
                value={localSubtitle}
                onChange={(e) => handleGlobalFieldChange('sectionSubtitle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none"
                placeholder="Trusted by leading organizations"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2 border-t border-slate-100 items-end">
            <div>
              <label className="block text-xs font-semibold text-slate-600 mb-1.5">Display Layout Alignment</label>
              <select
                value={localDisplayStyle}
                onChange={(e) => handleGlobalFieldChange('displayStyle', e.target.value)}
                className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-xl bg-slate-50/50 focus:bg-white focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all outline-none appearance-none"
              >
                <option value="logo-wall">Logo Wall Banner</option>
                <option value="grid">Normal Grid Layout</option>
                <option value="carousel">Carousel View Slider</option>
              </select>
            </div>

            <div className="md:col-span-2 bg-slate-50 rounded-xl p-3 border border-slate-150 grid grid-cols-2 gap-3">
              {[
                { label: 'Show Number of Hires', key: 'showHires', checked: localCheckboxes.showHires },
                { label: 'Show Categories', key: 'showCategories', checked: localCheckboxes.showCategories },
              ].map((checkbox) => (
                <label key={checkbox.key} className="flex items-center space-x-2.5 cursor-pointer group select-none">
                  <input
                    type="checkbox"
                    checked={checkbox.checked}
                    onChange={() => handleLocalCheckboxToggle(checkbox.key)}
                    className="sr-only"
                  />
                  <div className={`w-5 h-5 rounded-md flex items-center justify-center transition-all border ${
                    checkbox.checked 
                      ? 'bg-blue-600 border-blue-600 text-white' 
                      : 'bg-white border-slate-300 text-transparent group-hover:border-slate-400'
                  }`}>
                    <span className={`w-2 h-2 rounded-sm bg-white ${checkbox.checked ? 'block' : 'hidden'}`} />
                  </div>
                  <span className="text-xs font-medium text-slate-600 group-hover:text-slate-900 transition-colors">
                    {checkbox.label}
                  </span>
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Corporate List Mapping Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between px-1">
            <h3 className="text-sm font-bold text-slate-800">Added Enterprise Companies</h3>
            <span className="text-xs font-semibold bg-slate-200 text-slate-600 px-2.5 py-0.5 rounded-full">
              {localItems.length} Total Partners
            </span>
          </div>

          <div className="space-y-4">
            {localItems.map((item, index) => {
              const resolvedLogo = resolveAssetUrl(item.logo);

              return (
                <div key={item._id || index} className="bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden">
                  <div className="p-4 sm:p-5 space-y-4">
                    
                    {/* Input Field Form Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Company Legal Name</span>
                        <input
                          type="text"
                          value={item.name || ''}
                          onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                          placeholder="Company Name"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Industry Vertical</span>
                        <select
                          value={item.category || 'Technology'}
                          onChange={(e) => handleItemChange(index, 'category', e.target.value)}
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500 appearance-none"
                        >
                          {categories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>

                      <div className="space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Total Recruited Hires</span>
                        <input
                          type="number"
                          value={item.hires ?? ''}
                          onChange={(e) => handleItemChange(index, 'hires', e.target.value)}
                          placeholder="Hires count"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>

                      <div className="col-span-1 sm:col-span-2 md:col-span-3 space-y-1">
                        <span className="text-[10px] font-bold uppercase text-slate-400">Corporate Portal Link URL</span>
                        <input
                          type="text"
                          value={item.link || ''}
                          onChange={(e) => handleItemChange(index, 'link', e.target.value)}
                          placeholder="Corporate Website Link URL"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none bg-slate-50/30 focus:bg-white focus:border-blue-500"
                        />
                      </div>
                    </div>

                    {/* Image File Selector Wrapper Component */}
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4 bg-slate-50 p-3 rounded-xl border border-slate-150">
                      <div className="flex-shrink-0 mx-auto sm:mx-0">
                        <div className="w-16 h-16 rounded-xl border border-slate-200 flex items-center justify-center overflow-hidden bg-white shadow-inner relative group">
                          {resolvedLogo ? (
                            <div className="w-full h-full p-1 relative">
                              <img 
                                src={resolvedLogo} 
                                alt={item.name || "Company Logo"} 
                                className="w-full h-full object-contain"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveLogo(index)}
                                className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-white rounded-xl"
                              >
                                <FiX className="h-4 w-4" />
                              </button>
                            </div>
                          ) : (
                            <FiImage className="h-6 w-6 text-slate-400" />
                          )}
                        </div>
                      </div>
                      <div className="text-center sm:text-left flex-1">
                        <input
                          type="file"
                          id={`recruiter-logo-${index}`}
                          accept="image/jpeg,image/png,image/webp,image/svg+xml"
                          onChange={(e) => handleLogoUpload(e, index)}
                          disabled={uploading[index]}
                          className="hidden"
                        />
                        <label
                          htmlFor={`recruiter-logo-${index}`}
                          className={`inline-flex items-center px-3 py-1.5 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white hover:bg-slate-50 shadow-sm cursor-pointer transition-all select-none ${
                            uploading[index] ? 'opacity-50 cursor-not-allowed' : 'active:scale-95'
                          }`}
                        >
                          {uploading[index] ? 'Uploading...' : (resolvedLogo ? 'Change Brand Logo' : 'Upload Brand Logo')}
                        </label>
                        <p className="text-[10px] text-slate-400 mt-1">Accepts JPG, PNG, WEBP, SVG under 2MB</p>
                      </div>
                    </div>

                    <div className="mt-2 flex justify-end border-t border-slate-100 pt-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="text-rose-500 hover:text-rose-700 font-semibold text-xs flex items-center px-2 py-1 rounded-md hover:bg-rose-50 transition-colors"
                      >
                        <FiTrash2 className="mr-1 h-3.5 w-3.5" />
                        Delete Partner
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Create New Block Section Form Container */}
          <div className="bg-white border-2 border-dashed border-slate-200 rounded-xl p-4 sm:p-5 shadow-sm">
            <div className="flex items-center space-x-2 mb-3">
              <div className="p-1 bg-blue-50 rounded-md text-blue-600">
                <FiPlus className="h-4 w-4 stroke-[3]" />
              </div>
              <h4 className="text-sm font-bold text-slate-800">Create New Recruiter Partner</h4>
            </div>
            
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newItem.name}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                  placeholder="Company Name *"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
                <select
                  value={newItem.category}
                  onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                >
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
                <input
                  type="number"
                  value={newItem.hires === 0 ? '' : newItem.hires}
                  onChange={(e) => setNewItem({ ...newItem, hires: Number(e.target.value) })}
                  placeholder="Total Placement Hires"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
              </div>
              
              <div className="flex flex-col sm:flex-row items-end gap-3">
                <input
                  type="text"
                  value={newItem.link}
                  onChange={(e) => setNewItem({ ...newItem, link: e.target.value })}
                  placeholder="Corporate Website Portal Link URL"
                  className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-blue-500"
                />
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-5 py-2 bg-slate-900 text-white font-semibold rounded-lg text-sm hover:bg-slate-800 transition-all active:scale-[0.98] shadow-sm whitespace-nowrap"
                >
                  Insert Partner
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitersSection;